'use client'

import type { ReactNode } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { useAuth } from '@/components/AuthProvider'
import { isMarketingPath } from '@/lib/route-gates'
import { useDemoStatus } from '@/hooks/useDemoStatus'
import { PendingApprovalScreen } from '@/components/PendingApprovalScreen'

/**
 * Blocks the authed app for demo accounts that are not yet usable:
 *   pending_approval → "your access is being reviewed"
 *   rejected         → "access not granted"
 *
 * Locked demo accounts (limit reached) are NOT hard-blocked here — those keep
 * read-only access to their own history and get the paywall banner/modal
 * (handled in the demo enforcement UI). The server independently enforces all
 * of these states on the analyze path, so this component is UX, not security.
 *
 * Sits inside the authed tree (below AuthProvider). Passes through immediately
 * on marketing/public routes and while the status is still loading, so it
 * never adds a blocking spinner to normal navigation.
 */
export function AccountGate({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const { user } = useAuth()
  const { demo, isLoading } = useDemoStatus()

  if (isMarketingPath(pathname) || !user?.id) return <>{children}</>
  // Don't gate until we actually know the status (avoids a flash for the
  // overwhelmingly common full/active account).
  if (isLoading || !demo) return <>{children}</>

  if (demo.accountStatus === 'pending_approval') return <PendingApprovalScreen />
  if (demo.accountStatus === 'rejected') return <PendingApprovalScreen rejected />

  return <>{children}</>
}
